import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../api';
import { ToastContainer, toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import RowDelete from '../ui/RowDelete';
import { FilterInterface } from '../types';
import PageModal from './Page';
import NewPage from './NewPage';
import useAnalytics from '../analytics/analytics';
import Spinner from '../documentation/Spinner';
import { formatDate } from '../utils/dateFormatter';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Lock, Unlock, X, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

interface PagesProps {
  websiteId?: string;
}

const initialFilters: FilterInterface[] = [
  {
    id: 'indexed',
    name: 'Indexed',
    options: [
      { value: 'true', label: 'Yes', checked: false },
      { value: 'false', label: 'No', checked: false }
    ],
  },
  {
    id: 'index_status',
    name: 'Status',
    options: [
      { value: 'Completed', label: 'Completed', checked: false },
      { value: 'Failed', label: 'Failed', checked: false },
      { value: 'Not Found', label: 'Not Found', checked: false },
      { value: 'Archived', label: 'Archived', checked: false }
    ],
  }
];

const IndexStatusBadge = ({ status }: { status: string }) => {
  const getVariant = () => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'indexed':
        return 'success';
      case 'failed':
        return 'destructive';
      case 'not found':
        return 'destructive';
      case 'archived':
        return 'secondary';
      case 'queued':
      case 'in progress':
      case 'indexing':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  return (
    <Badge variant={getVariant() as any}>
      {status || 'Not Indexed'}
    </Badge>
  );
};

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Completed', label: 'Completed' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Not Found', label: 'Not Found' },
  { value: 'Archived', label: 'Archived' }
];

const perPageOptions = [
  { value: 10, label: '10 per page' },
  { value: 25, label: '25 per page' },
  { value: 50, label: '50 per page' },
  { value: 100, label: '100 per page' },
  { value: 200, label: '200 per page' },
  { value: 500, label: '500 per page' }
];

const Pages: React.FC<PagesProps> = ({ websiteId }) => {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const filtersRef = useRef<FilterInterface[]>(initialFilters);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestrictDialog, setShowRestrictDialog] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<'restrict' | 'release' | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restrictLoading, setRestrictLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [perPage, setPerPage] = useState<number>(25);
  const navigate = useNavigate();
  const location = useLocation();
  const { get, remove, post } = useApi();
  const { captureEvent } = useAnalytics();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentPage = parseInt(params.get('page') || '1');
    const currentSearch = params.get('search') || '';
    const currentStatus = params.get('index_status') || null;
    const currentSortBy = params.get('sort_by') || null;
    const currentSortOrder = (params.get('sort_order') || 'desc') as 'asc' | 'desc';
    const currentPerPage = parseInt(params.get('per_page') || '25');
    const currentFilters = filters.map(filter => ({
      ...filter,
      options: filter.options.map(option => ({
        ...option,
        checked: params.get(filter.id) === option.value
      }))
    }));
    setPage(currentPage);
    setSearchInput(currentSearch);
    setStatusFilter(currentStatus);
    setSortBy(currentSortBy);
    setSortOrder(currentSortOrder);
    setPerPage(currentPerPage);
    setFilters(currentFilters);
    filtersRef.current = currentFilters;
    getPages(currentPage, currentSearch, currentFilters, currentStatus, currentSortBy, currentSortOrder, currentPerPage);
  }, [location.search]);

  const getPages = async (page: number = 1, search: string = '', filters: FilterInterface[] = [], status: string | null = null, sortByParam: string | null = null, sortOrderParam: 'asc' | 'desc' = 'desc', perPageParam: number = 25) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPageParam.toString(),
        search,
      });
      if (websiteId) {
        params.append('website_id', websiteId);
      }
      if (status && status !== 'all') {
        params.append('index_status', status);
      }
      if (sortByParam) {
        params.append('sort_by', sortByParam);
        params.append('sort_order', sortOrderParam);
      }
      filters.forEach(filter => {
        filter.options.forEach(option => {
          if (option.checked) {
            params.append(filter.id, option.value);
          }
        });
      });
      const data = await get(`/pages/?${params.toString()}`);
      setPages(data.data.items);
      setPage(data.data.page);
      setTotalPages(data.data.pages);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
    setLoading(false);
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      const params = new URLSearchParams(location.search);
      params.set('page', (page + 1).toString());
      filtersRef.current.forEach(filter => {
        const selectedOption = filter.options.find(option => option.checked);
        if (selectedOption) {
          params.set(filter.id, selectedOption.value);
        }
      });
      navigate(`?${params.toString()}`);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      const params = new URLSearchParams(location.search);
      params.set('page', (page - 1).toString());
      filtersRef.current.forEach(filter => {
        const selectedOption = filter.options.find(option => option.checked);
        if (selectedOption) {
          params.set(filter.id, selectedOption.value);
        }
      });
      navigate(`?${params.toString()}`);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await remove(`/pages/${id}`);
      setPages(pages.filter((page: any) => page.id !== id));
      setSelectedPages(selectedPages.filter((selectedId) => selectedId !== id));
      toast.success('Page deleted successfully', toastConfig);
      captureEvent('page_deleted', {
        page_id: id,
      });
    } catch (error) {
      console.error('Error deleting page:', error);
    }
    setLoading(false);
  };

  const handleSearch = (search: string) => {
    const params = new URLSearchParams({ search, page: '1', per_page: perPage.toString() });
    filters.forEach(filter => {
      const selectedOption = filter.options.find(option => option.checked);
      if (selectedOption) {
        params.set(filter.id, selectedOption.value);
      }
    });
    if (websiteId) {
      params.set('website_id', websiteId);
    }
    navigate(`?${params.toString()}`);
  };

  const handleFilterChange = (newFilters: FilterInterface[]) => {
    setFilters(newFilters);
    filtersRef.current = newFilters;
    const params = new URLSearchParams({ search: searchInput, page: '1', per_page: perPage.toString() });
    newFilters.forEach(filter => {
      const selectedOption = filter.options.find(option => option.checked);
      if (selectedOption) {
        params.set(filter.id, selectedOption.value);
      }
    });
    if (websiteId) {
      params.set('website_id', websiteId);
    }
    navigate(`?${params.toString()}`);
  };

  const handleStatusChange = (value: string) => {
    const params = new URLSearchParams({ search: searchInput, page: '1', per_page: perPage.toString() });
    if (value && value !== 'all') {
      params.set('index_status', value);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
    }
    if (websiteId) {
      params.set('website_id', websiteId);
    }
    navigate(`?${params.toString()}`);
  };

  const handleSort = (column: string) => {
    const params = new URLSearchParams({ search: searchInput, page: '1', per_page: perPage.toString() });
    
    let newSortOrder: 'asc' | 'desc' = 'desc';
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    }
    
    params.set('sort_by', column);
    params.set('sort_order', newSortOrder);
    
    if (statusFilter && statusFilter !== 'all') {
      params.set('index_status', statusFilter);
    }
    if (websiteId) {
      params.set('website_id', websiteId);
    }
    navigate(`?${params.toString()}`);
  };

  const handlePerPageChange = (value: string) => {
    const params = new URLSearchParams({ search: searchInput, page: '1', per_page: value });
    if (statusFilter && statusFilter !== 'all') {
      params.set('index_status', statusFilter);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
    }
    if (websiteId) {
      params.set('website_id', websiteId);
    }
    navigate(`?${params.toString()}`);
  };

  const handleRowClick = (id: string) => {
    if (!id) return;
    setSelectedPageId(id);
  };

  const closeModal = () => {
    setSelectedPageId(null);
  };

  const handleReindex = async (page_url: string) => {
    try {
      setLoading(true);
      await post(`/index-page/${websiteId}`, { url: page_url });
      toast.success('Training started!', toastConfig);
      captureEvent('reindexed_page', {
        page_url,
      });
    } catch (error: any) {
      toast.error(error.detail, toastConfig);
      console.error('Training error:', error);
    }
    setLoading(false);

  };

  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const allSelected = pages.every((page: any) => selectedPages.includes(page.id));
      const someSelected = pages.some((page: any) => selectedPages.includes(page.id));
      selectAllCheckboxRef.current.indeterminate = !allSelected && someSelected;
    }
  }, [pages, selectedPages]);

  const areAllCurrentPageSelected = pages.every((page: any) => selectedPages.includes(page.id));

  const handleSelectAllCurrentPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = [...selectedPages];
      pages.forEach((page: any) => {
        if (!newSelected.includes(page.id)) {
          newSelected.push(page.id);
        }
      });
      setSelectedPages(newSelected);
    } else {
      setSelectedPages(selectedPages.filter((id: string) => !pages.some((page: any) => page.id === id)));
    }
  };

  const handleSelectRow = (id: string) => {
    if (selectedPages.includes(id)) {
      setSelectedPages(selectedPages.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedPages([...selectedPages, id]);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setDeleteLoading(true);
      const pageCount = selectedPages.length;
      
      await post(`/pages/bulk-action`, { 
        page_ids: selectedPages, 
        action: 'delete' 
      });
      
      // Remove deleted pages from the list
      setPages(pages.filter((page: any) => !selectedPages.includes(page.id)));
      setSelectedPages([]);
      toast.success(`Successfully deleted ${pageCount} page(s)`, toastConfig);
      captureEvent('bulk_pages_deleted', {
        count: pageCount,
      });
      
      // Close dialog after successful deletion
      setShowDeleteDialog(false);
      
      // Refresh the page list
      getPages(page, searchInput, filters, statusFilter, sortBy, sortOrder, perPage);
    } catch (error) {
      console.error('Error deleting pages:', error);
      toast.error('Failed to delete pages', toastConfig);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkRestrict = async () => {
    try {
      setRestrictLoading(true);
      const action = bulkActionType === 'restrict' ? 'restrict_pages' : 'release_pages';
      
      // Filter pages based on action type
      let pagesToActOn = pages.filter((page: any) => selectedPages.includes(page.id));
      if (bulkActionType === 'restrict') {
        pagesToActOn = pagesToActOn.filter((page: any) => 
          !(page.page_config && page.page_config.current_page_only)
        );
      } else {
        pagesToActOn = pagesToActOn.filter((page: any) => 
          page.page_config && page.page_config.current_page_only
        );
      }
      
      if (pagesToActOn.length === 0) {
        toast.info('No pages to update', toastConfig);
        setShowRestrictDialog(false);
        setBulkActionType(null);
        setRestrictLoading(false);
        return;
      }
      
      await post(`/pages/bulk-action`, { 
        page_ids: pagesToActOn.map((p: any) => p.id), 
        action 
      });
      
      setSelectedPages([]);
      
      const message = bulkActionType === 'restrict' 
        ? `Restricted ${pagesToActOn.length} page(s) to current page only`
        : `Released ${pagesToActOn.length} page(s) from current page restriction`;
      
      toast.success(message, toastConfig);
      captureEvent('bulk_pages_restriction_changed', {
        action: bulkActionType,
        count: pagesToActOn.length,
      });
      
      // Close dialog after successful update
      setShowRestrictDialog(false);
      setBulkActionType(null);
      
      // Refresh the page list
      getPages(page, searchInput, filters, statusFilter, sortBy, sortOrder, perPage);
    } catch (error) {
      console.error('Error updating pages:', error);
      toast.error('Failed to update pages', toastConfig);
    } finally {
      setRestrictLoading(false);
    }
  };

  const getSelectedPagesInfo = () => {
    const selectedPageData = pages.filter((page: any) => selectedPages.includes(page.id));
    const restrictedCount = selectedPageData.filter((page: any) => 
      page.page_config && page.page_config.current_page_only
    ).length;
    const unrestrictedCount = selectedPageData.length - restrictedCount;
    
    return { total: selectedPageData.length, restricted: restrictedCount, unrestricted: unrestrictedCount };
  };

  return (
    <div className="flex flex-col w-full scrollbar-hidden">
      {selectedPageId && <PageModal isOpen={!!selectedPageId} closeModal={closeModal} pageId={selectedPageId} />}
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => !deleteLoading && setShowDeleteDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Pages</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedPages.length} selected page(s)? 
              This action cannot be undone and will remove the pages from your training data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restrict/Release Confirmation Dialog */}
      <AlertDialog open={showRestrictDialog} onOpenChange={(open) => !restrictLoading && setShowRestrictDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkActionType === 'restrict' ? 'Restrict Pages' : 'Release Pages'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkActionType === 'restrict' 
                ? `This will restrict ${getSelectedPagesInfo().unrestricted} page(s) to be searchable only when users are on those specific pages.`
                : `This will release ${getSelectedPagesInfo().restricted} page(s) from the current page restriction, making them searchable from any page.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => !restrictLoading && setBulkActionType(null)}
              disabled={restrictLoading}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkRestrict}
              disabled={restrictLoading}
            >
              {restrictLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {bulkActionType === 'restrict' ? 'Restricting...' : 'Releasing...'}
                </>
              ) : (
                bulkActionType === 'restrict' ? 'Restrict Pages' : 'Release Pages'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className={`w-full ${websiteId ? '' : 'mt-12'} grow`}>
        <div className='flex flex-col w-full'>
          {/* Bulk Actions Toolbar */}
          {selectedPages.length > 0 && (
            <div className="mb-4 p-4 bg-muted/50 rounded-lg border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {selectedPages.length} page{selectedPages.length > 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedPages([])}
                    className="h-8 px-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  {/* {getSelectedPagesInfo().unrestricted > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setBulkActionType('restrict');
                        setShowRestrictDialog(true);
                      }}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      Restrict to Current Page ({getSelectedPagesInfo().unrestricted})
                    </Button>
                  )} */}
                  {getSelectedPagesInfo().restricted > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setBulkActionType('release');
                        setShowRestrictDialog(true);
                      }}
                    >
                      <Unlock className="h-4 w-4 mr-2" />
                      Release Restriction ({getSelectedPagesInfo().restricted})
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className='flex flex-row justify-between'>
            <div className='flex items-center w-full gap-4'>
              <Input
                className="grow max-w-full"
                type="text"
                id="search"
                placeholder="Search pages by URL or meta..."
                autoComplete='off'
                value={searchInput}
                onChange={(e: any) => setSearchInput(e.target.value)}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    handleSearch(e.currentTarget.value);
                  }
                }}
              />
              <div className="w-48">
                <Select
                  value={statusFilter || 'all'}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue>
                      {statusFilter
                        ? statusOptions.find(opt => opt.value === statusFilter)?.label || 'All Statuses'
                        : 'All Statuses'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='max-w-1/3 flex-row'>
                {websiteId && <NewPage websiteId={websiteId} />}
              </div>
            </div>
          </div>
            <div className="flex flex-col mt-4 min-h-full scrollbar-hidden mb-8">
              <div className="-m-1.5 overflow-x-auto">
                <div className="p-1.5 min-w-full inline-block align-middle">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <input
                            type="checkbox"
                            checked={areAllCurrentPageSelected}
                            onChange={handleSelectAllCurrentPage}
                            ref={selectAllCheckboxRef}
                          />
                        </TableHead>
                        <TableHead className="w-12"></TableHead>
                        <TableHead className="w-1/4">URL</TableHead>
                        <TableHead>
                          <button
                            onClick={() => handleSort('indexed_at')}
                            className="flex items-center gap-1 hover:text-foreground transition-colors"
                          >
                            Last Trained
                            {sortBy === 'indexed_at' ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp className="h-4 w-4" />
                              ) : (
                                <ArrowDown className="h-4 w-4" />
                              )
                            ) : (
                              <ArrowUpDown className="h-4 w-4 opacity-50" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>Characters</TableHead>
                        <TableHead>Training Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pages.length > 0 &&
                        pages.map((page: any) => (
                          <TableRow key={page.id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedPages.includes(page.id)}
                                onChange={() => handleSelectRow(page.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="secondary"
                                onClick={() => handleRowClick(page.id)}
                                disabled={page.index_status === 'Not Found' || page.index_status === 'Archived'}
                              >
                                View
                              </Button>
                            </TableCell>
                            <TableCell className="break-all">
                              {page.url}
                            </TableCell>
                            <TableCell>
                              {page.indexed_at ? formatDate(page.indexed_at) : ''}
                            </TableCell>
                            <TableCell>
                              {page.tokens || '-'}
                            </TableCell>
                            <TableCell>
                              <IndexStatusBadge status={page.index_status} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end items-center gap-2">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleReindex(page.url)}
                                >
                                  {page.index_status === 'Archived' ? 'Restore' : 'Train'}
                                </Button>
                                <RowDelete deleteCallback={() => handleDelete(page.id)} />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      {pages.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-16">
                            No pages
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div className="flex justify-between items-center mt-4">
                <Button
                  variant="outline"
                  onClick={handlePreviousPage}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm font-semibold">
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center gap-4">
                  <div className="w-40">
                    <Select
                      value={perPage.toString()}
                      onValueChange={handlePerPageChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {perPageOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value.toString()}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleNextPage}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Pages;